import type { QuestionDefinition } from "@reno-estimator/core";
import { BATHROOM_SIZE_RANGES } from "../bathroom-size-ranges.js";

/**
 * Bathroom's full question set (Phase 6 - the second fully-built-out
 * project type, following kitchen's Phase 5A pattern). Every question
 * here maps directly onto a `rawAnswers` key the bathroom rule
 * calculator reads (see packages/core/src/calculation/rules/bathroom.ts)
 * and renders through the same generic `QuestionRenderer`/
 * `ProjectSpecificQuestions` pair kitchen already uses - no wizard code
 * change was needed to add this project type's own questions, only this
 * config file plus the matching pricing line items and rule-calculator
 * mapping.
 *
 * Progressive disclosure: only `bathroom_existing_configuration` and
 * `bathroom_shower_scope` are required beyond the common questions
 * (location/scope/finish level/etc. - see common-questions.ts) - these
 * two are what actually distinguish "keep the bathroom mostly as-is" from
 * "replace the tub/shower," which is normally the single biggest cost
 * driver in a bathroom renovation. Everything else defaults to a
 * conservative assumption when skipped (see bathroom.ts's own doc
 * comment for exactly what each category defaults to) rather than
 * blocking the wizard.
 *
 * `bathroom_electrical_scope` is gated behind the common
 * `electrical_changes` yes/no/unknown flag, same as kitchen's equivalent
 * - there's no "no change" option in its own list, so it only makes sense
 * to ask once the user has said there's electrical work happening at all.
 * `bathroom_plumbing_scope`, by contrast, is NOT gated the same way: since
 * a bathroom renovation is fundamentally about its fixtures, its own
 * option list includes "Retain existing layout" as the first choice, so
 * it's always safe (and useful) to show regardless of the common
 * `plumbing_changes` flag.
 */

const BATHROOM_SIZE_RANGE_OPTIONS = BATHROOM_SIZE_RANGES.map((r) => ({ id: r.id, label: r.label }));

export const bathroomQuestions: QuestionDefinition[] = [
  {
    id: "bathroom_size_range",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "How large is the bathroom?",
    helpText: "A rough range is fine - you can enter an exact square footage instead if you know it.",
    required: false,
    options: BATHROOM_SIZE_RANGE_OPTIONS,
    order: 15
  },
  {
    id: "bathroom_existing_configuration",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "What's the current tub/shower configuration?",
    required: true,
    options: [
      { id: "tub", label: "Tub only" },
      { id: "tub_shower_combo", label: "Tub / shower combination" },
      { id: "standard_shower", label: "Standard shower stall" },
      { id: "walk_in_shower", label: "Walk-in shower" },
      { id: "other", label: "Other" }
    ],
    order: 200
  },
  {
    id: "bathroom_shower_scope",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "What's happening with the tub/shower?",
    required: true,
    options: [
      { id: "keep_existing", label: "Keep existing" },
      { id: "replace_like_for_like", label: "Replace like-for-like" },
      { id: "upgrade", label: "Upgrade" },
      { id: "custom_shower", label: "Custom shower" },
      { id: "premium_custom", label: "Premium / custom solution" }
    ],
    order: 205
  },
  {
    id: "bathroom_vanity",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "What's happening with the vanity?",
    required: false,
    options: [
      { id: "keep_existing", label: "Keep existing" },
      { id: "standard_replacement", label: "Standard replacement" },
      { id: "double_vanity", label: "Double vanity" },
      { id: "semi_custom", label: "Semi-custom" },
      { id: "custom", label: "Custom" }
    ],
    order: 210
  },
  {
    id: "bathroom_toilet",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "What's happening with the toilet?",
    required: false,
    options: [
      { id: "keep_existing", label: "Keep existing" },
      { id: "replace_standard", label: "Replace with standard" },
      { id: "premium_specialty", label: "Premium / specialty" }
    ],
    order: 215
  },
  {
    id: "bathroom_tile_scope",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "What's the extent of tile work?",
    helpText: "This drives both how much tile area is priced and (with finish level) what quality of tile.",
    required: false,
    options: [
      { id: "no_tile_changes", label: "No tile changes" },
      { id: "basic_floor_tile", label: "Basic floor tile" },
      { id: "floor_and_shower_surround", label: "Floor and shower surround" },
      { id: "full_height_shower_tile", label: "Full-height shower tile" },
      { id: "extensive_custom_tile", label: "Extensive / custom tile" }
    ],
    order: 220
  },
  {
    id: "bathroom_glass_type",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "What kind of shower glass enclosure?",
    required: false,
    options: [
      { id: "none", label: "None" },
      { id: "standard", label: "Standard" },
      { id: "frameless", label: "Frameless" },
      { id: "custom", label: "Custom" }
    ],
    order: 225
  },
  {
    id: "bathroom_flooring_type",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "What's happening with the (non-tile) flooring?",
    helpText: "If flooring is tile, use the tile question above instead - this is for a separate, non-tile flooring change.",
    required: false,
    options: [
      { id: "no_change", label: "No change" },
      { id: "standard_replacement", label: "Standard replacement" },
      { id: "mid_range", label: "Mid-range" },
      { id: "premium", label: "Premium" }
    ],
    order: 230
  },
  {
    id: "bathroom_plumbing_scope",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "What kind of plumbing work does this involve?",
    required: false,
    options: [
      { id: "retain_existing_layout", label: "Retain existing layout" },
      { id: "minor_changes", label: "Minor changes" },
      { id: "fixture_relocation", label: "Fixture relocation" },
      { id: "major_relocation", label: "Major relocation" }
    ],
    order: 235
  },
  {
    id: "bathroom_electrical_scope",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "What kind of electrical and lighting work does this involve?",
    required: false,
    displayIf: [{ questionId: "electrical_changes", equalsAnyOf: ["yes", "unknown"] }],
    options: [
      { id: "minor_updates", label: "Minor updates" },
      { id: "new_lighting", label: "New lighting" },
      { id: "additional_circuits", label: "Additional circuits / outlets" },
      { id: "significant_changes", label: "Significant changes" }
    ],
    order: 240
  },
  {
    id: "bathroom_heated_floor",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "boolean",
    prompt: "Include in-floor heating?",
    required: false,
    order: 245
  },
  {
    id: "bathroom_ventilation_scope",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "single_select",
    prompt: "What's happening with ventilation?",
    required: false,
    options: [
      { id: "existing_retained", label: "Existing retained" },
      { id: "upgrade", label: "Upgrade" },
      { id: "new_additional", label: "New / additional ventilation work" }
    ],
    order: 250
  },
  {
    id: "bathroom_custom_cabinetry",
    projectTypes: ["bathroom", "ensuite"],
    inputType: "boolean",
    prompt: "Include custom cabinetry (e.g. a built-in linen cabinet)?",
    required: false,
    order: 255
  }
];
